'use client';

import React, { useState, useEffect } from 'react';
import {
  Eye,
  EyeOff,
  Shield,
  Briefcase,
  FileText,
  Scale,
  BookOpen,
  AlertCircle,
  ShieldCheck,
  ArrowLeft,
  Mail,
  CheckCircle,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';

const SERVICES = [
  { icon: Shield, label: 'Insurance' },
  { icon: Briefcase, label: 'Business Services' },
  { icon: FileText, label: 'Estate Planning' },
  { icon: Scale, label: 'Legal Documents' },
  { icon: BookOpen, label: 'Tax Services' },
];

function InlineError({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div className="flex items-start gap-2 mt-2 p-2.5 bg-red-50 border border-red-200 rounded-lg">
      <AlertCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
      <p className="text-red-600 text-xs leading-snug">{message}</p>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

export default function LoginScreen() {
  const [activeTab, setActiveTab] = useState<'signin' | 'signup'>('signin');
  const [showPassword, setShowPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Sign-in state
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signInLoading, setSignInLoading] = useState(false);
  const [signInError, setSignInError] = useState('');

  // Google state
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleError, setGoogleError] = useState('');

  // Sign-up state
  const [signUpFullName, setSignUpFullName] = useState('');
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpConfirm, setSignUpConfirm] = useState('');
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpError, setSignUpError] = useState('');
  const [signUpSuccess, setSignUpSuccess] = useState(false);

  // 2FA state
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [twoFAStaffId, setTwoFAStaffId] = useState('');
  const [twoFAEmail, setTwoFAEmail] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');
  const [twoFASent, setTwoFASent] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState(false);

  // Session timeout message
  const [timeoutMessage, setTimeoutMessage] = useState('');

  // Store credentials in a ref so they persist through the 2FA flow without triggering re-renders
  const credentialsRef = React.useRef<{ email: string; password: string } | null>(null);

  const { signInWithGoogle, signUp, signIn, signOut } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'timeout') {
      setTimeoutMessage('You were automatically signed out due to inactivity.');
    }
  }, []);

  // ── Google OAuth ──────────────────────────────────────────────────────────
  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setGoogleError('');
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setGoogleError(err?.message || 'Google sign-in failed. Please try again.');
      setGoogleLoading(false);
    }
  };

  // ── Email / Password Sign-In ──────────────────────────────────────────────
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signInEmail || !signInPassword) {
      setSignInError('Please enter your email and password.');
      return;
    }
    setSignInLoading(true);
    setSignInError('');
    try {
      const authData = await signIn(signInEmail, signInPassword);

      // Check if 2FA is enabled via API route (avoids competing Supabase auth locks)
      if (authData.user) {
        const res = await fetch('/api/auth/2fa-check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: authData.user.id }),
        });
        const data = await res.json();

        if (data.twoFAEnabled && data.staffId) {
          // Keep user signed in — just require OTP verification before proceeding
          setTwoFAStaffId(data.staffId);
          setTwoFAEmail(data.email || signInEmail);
          // Send OTP
          const otpRes = await fetch('/api/auth/2fa', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'send', staffId: data.staffId }),
          });
          if (otpRes.ok) setTwoFASent(true);
          setTwoFARequired(true);
          return;
        }
      }

      window.location.href = '/crm-dashboard';
    } catch (err: any) {
      const msg = (err?.message || '').toLowerCase();
      if (
        msg.includes('rate limit') ||
        msg.includes('too many') ||
        msg.includes('over_email_send_rate_limit') ||
        msg.includes('email rate limit')
      ) {
        setSignInError(
          'Sign-in rate limit reached. Supabase limits login attempts per hour. Please wait at least 60 minutes before trying again, or contact your administrator.'
        );
      } else if (msg.includes('invalid login credentials') || msg.includes('invalid credentials')) {
        setSignInError('Incorrect email or password. Please check your credentials and try again.');
      } else {
        setSignInError(err?.message || 'Sign in failed. Please try again.');
      }
    } finally {
      setSignInLoading(false);
    }
  };

  // ── 2FA Verify ───────────────────────────────────────────────────────────
  const handle2FAVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFACode || twoFACode.length !== 6) {
      setTwoFAError('Please enter the 6-digit code.');
      return;
    }
    setTwoFALoading(true);
    setTwoFAError('');
    try {
      const res = await fetch('/api/auth/2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify', staffId: twoFAStaffId, code: twoFACode }),
      });
      const data = await res.json();
      if (!res.ok) {
        setTwoFAError(data.error || 'Invalid code. Please try again.');
        return;
      }
      // OTP verified — user is already signed in, just redirect
      window.location.href = '/crm-dashboard';
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.toLowerCase().includes('rate limit') || msg.toLowerCase().includes('too many')) {
        setTwoFAError('Too many attempts. Please wait 60 seconds, then go back and sign in again.');
      } else {
        setTwoFAError(msg || 'Verification failed.');
      }
    } finally {
      setTwoFALoading(false);
    }
  };

  const handle2FAResend = async () => {
    setTwoFAError('');
    const res = await fetch('/api/auth/2fa', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'send', staffId: twoFAStaffId }),
    });
    if (res.ok) setTwoFASent(true);
  };

  // ── Sign Up ───────────────────────────────────────────────────────────────
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUpFullName || !signUpEmail || !signUpPassword || !signUpConfirm) {
      setSignUpError('Please fill in all fields.');
      return;
    }
    if (signUpPassword !== signUpConfirm) {
      setSignUpError('Passwords do not match.');
      return;
    }
    if (signUpPassword.length < 6) {
      setSignUpError('Password must be at least 6 characters.');
      return;
    }
    setSignUpLoading(true);
    setSignUpError('');
    try {
      await signUp(signUpEmail, signUpPassword, { fullName: signUpFullName });
      setSignUpSuccess(true);
    } catch (err: any) {
      setSignUpError(err?.message || 'Sign up failed. Please try again.');
    } finally {
      setSignUpLoading(false);
    }
  };

  // ── Forgot Password ───────────────────────────────────────────────────────
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) {
      setForgotError('Please enter your email address.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail)) {
      setForgotError('Please enter a valid email address.');
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      if (!res.ok) {
        setForgotError(data.error || 'Failed to send reset email. Please try again.');
        return;
      }
      setForgotSuccess(true);
    } catch (err: any) {
      setForgotError('Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotError('');
    setForgotSuccess(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Brand Panel ── */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 bg-[#0A1F44] flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#C9A84C]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#C9A84C]/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] border border-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] border border-white/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <AppLogo src="/assets/images/LBFG_2026_LOGO_2-1775061184218.png" size={48} />
            <div>
              <span className="font-display text-xl font-bold text-white block leading-tight">
                LegacyBridge
              </span>
              <span className="text-[#C9A84C] text-xs tracking-widest uppercase">
                Financial Group
              </span>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex-1 flex flex-col justify-center py-12">
          <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
            Your Complete
            <br />
            <span className="text-[#C9A84C]">Financial Services</span>
            <br />
            Platform
          </h1>
          <p className="text-white/60 text-sm leading-relaxed mb-10 max-w-sm">
            Manage clients, automate workflows, and grow your practice — all in one secure platform.
          </p>
          <div className="grid grid-cols-1 gap-3">
            {SERVICES.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#C9A84C]/10 border border-[#C9A84C]/20 flex items-center justify-center flex-shrink-0">
                  <Icon size={14} className="text-[#C9A84C]" />
                </div>
                <span className="text-white/70 text-sm">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-white/30 text-xs">
            © 2026 LegacyBridge Financial Group. All rights reserved.
          </p>
        </div>
      </div>

      {/* ── Auth Panel ── */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-3 mb-8 justify-center">
            <AppLogo src="/assets/images/LBFG_2026_LOGO_2-1775061184218.png" size={40} />
            <div>
              <span className="font-bold text-[#0A1F44] text-lg block leading-tight">
                LegacyBridge
              </span>
              <span className="text-[#C9A84C] text-xs tracking-widest uppercase">
                Financial Group
              </span>
            </div>
          </div>

          {/* Session timeout message */}
          {timeoutMessage && (
            <div className="mb-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertCircle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-amber-700 text-xs">{timeoutMessage}</p>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* ── 2FA Verification Step ── */}
            {twoFARequired ? (
              <div className="p-8 space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#0A1F44] flex items-center justify-center">
                    <ShieldCheck size={20} className="text-[#C9A84C]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#0A1F44]">Two-Factor Authentication</h2>
                    <p className="text-gray-500 text-sm mt-0.5">
                      Enter the 6-digit code sent to your email
                    </p>
                  </div>
                </div>
                {twoFASent && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-700">
                    ✓ A verification code was sent to <strong>{twoFAEmail}</strong>
                  </div>
                )}
                <form onSubmit={handle2FAVerify} className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="w-full px-3.5 py-3 border border-gray-200 rounded-xl text-2xl font-mono text-center tracking-[0.5em] text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30 focus:border-[#C9A84C] transition-all"
                    />
                  </div>
                  <InlineError message={twoFAError} />
                  <button
                    type="submit"
                    disabled={twoFALoading}
                    className="w-full py-3 bg-[#0A1F44] text-white rounded-xl text-sm font-semibold hover:bg-[#0d2a5e] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {twoFALoading && <Spinner />}
                    {twoFALoading ? 'Verifying…' : 'Verify & Sign In'}
                  </button>
                </form>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <button
                    onClick={() => {
                      setTwoFARequired(false);
                      setTwoFACode('');
                      setTwoFAError('');
                    }}
                    className="hover:text-[#0A1F44]"
                  >
                    ← Back to sign in
                  </button>
                  <button
                    onClick={handle2FAResend}
                    className="text-[#C9A84C] hover:text-[#0A1F44] font-medium"
                  >
                    Resend code
                  </button>
                </div>
              </div>
            ) : showForgotPassword ? (
              /* ── Forgot Password Step ── */
              <div className="p-8 space-y-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#0A1F44] flex items-center justify-center">
                    <Mail size={20} className="text-[#C9A84C]" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#0A1F44]">Forgot Password</h2>
                    <p className="text-gray-500 text-sm mt-0.5">
                      We'll send a reset link to your email
                    </p>
                  </div>
                </div>

                {forgotSuccess ? (
                  <div className="text-center py-4 space-y-4">
                    <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                      <CheckCircle size={28} className="text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A1F44] mb-1">Check your inbox</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">
                        If an account exists for <strong>{forgotEmail}</strong>, a password reset
                        link has been sent.
                      </p>
                    </div>
                    <p className="text-gray-400 text-xs">
                      Didn't receive it? Check your spam folder.
                    </p>
                    <button
                      onClick={handleBackToSignIn}
                      className="w-full py-3 bg-[#0A1F44] text-white rounded-xl text-sm font-semibold hover:bg-[#0d2a5e] transition-colors flex items-center justify-center gap-2"
                    >
                      <ArrowLeft size={14} />
                      Back to Sign In
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoComplete="email"
                        className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30 focus:border-[#C9A84C] transition-all"
                      />
                    </div>

                    <InlineError message={forgotError} />

                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full py-3 bg-[#0A1F44] text-white rounded-xl text-sm font-semibold hover:bg-[#0d2a5e] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {forgotLoading && <Spinner />}
                      {forgotLoading ? 'Sending reset link…' : 'Send Reset Link'}
                    </button>

                    <button
                      type="button"
                      onClick={handleBackToSignIn}
                      className="w-full flex items-center justify-center gap-1.5 text-xs text-gray-500 hover:text-[#0A1F44] transition-colors py-1"
                    >
                      <ArrowLeft size={13} />
                      Back to Sign In
                    </button>
                  </form>
                )}
              </div>
            ) : (
              <>
                {/* Tab switcher */}
                <div className="flex border-b border-gray-200 bg-gray-100 rounded-t-2xl overflow-hidden">
                  <button
                    onClick={() => {
                      setActiveTab('signin');
                      setSignInError('');
                      setGoogleError('');
                    }}
                    className={`flex-1 py-4 text-sm font-semibold transition-colors ${
                      activeTab === 'signin'
                        ? 'text-[#0A1F44] border-b-2 border-[#C9A84C] bg-white'
                        : 'text-gray-600 hover:text-[#0A1F44] bg-gray-100 hover:bg-gray-50'
                    }`}
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('signup');
                      setSignUpError('');
                      setSignUpSuccess(false);
                    }}
                    className={`flex-1 py-4 text-sm font-semibold transition-colors ${
                      activeTab === 'signup'
                        ? 'text-[#0A1F44] border-b-2 border-[#C9A84C] bg-white'
                        : 'text-gray-600 hover:text-[#0A1F44] bg-gray-100 hover:bg-gray-50'
                    }`}
                  >
                    Create Account
                  </button>
                </div>

                <div className="p-8">
                  {/* ── Sign In Tab ── */}
                  {activeTab === 'signin' && (
                    <div className="space-y-5">
                      <div>
                        <h2 className="text-xl font-bold text-[#0A1F44]">Welcome back</h2>
                        <p className="text-gray-500 text-sm mt-1">
                          Sign in to your account to continue
                        </p>
                      </div>

                      {/* Google Sign-In */}
                      <button
                        onClick={handleGoogleSignIn}
                        disabled={googleLoading}
                        className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {googleLoading ? (
                          <Spinner />
                        ) : (
                          <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path
                              fill="#4285F4"
                              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                            />
                            <path
                              fill="#34A853"
                              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                            />
                            <path
                              fill="#EA4335"
                              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                            />
                          </svg>
                        )}
                        {googleLoading ? 'Redirecting to Google…' : 'Continue with Google'}
                      </button>

                      <InlineError message={googleError} />

                      {/* Divider */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="text-xs text-gray-400 font-medium">
                          or sign in with email
                        </span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>

                      {/* Email/Password form */}
                      <form onSubmit={handleSignIn} className="space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                            Email Address
                          </label>
                          <input
                            type="email"
                            value={signInEmail}
                            onChange={(e) => setSignInEmail(e.target.value)}
                            placeholder="you@example.com"
                            autoComplete="email"
                            className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30 focus:border-[#C9A84C] transition-all"
                          />
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-xs font-semibold text-gray-600">
                              Password
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                setForgotEmail(signInEmail);
                                setShowForgotPassword(true);
                              }}
                              className="text-xs text-[#C9A84C] hover:text-[#0A1F44] font-medium transition-colors"
                            >
                              Forgot password?
                            </button>
                          </div>
                          <div className="relative">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={signInPassword}
                              onChange={(e) => setSignInPassword(e.target.value)}
                              placeholder="••••••••"
                              autoComplete="current-password"
                              className="w-full px-3.5 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30 focus:border-[#C9A84C] transition-all"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((v) => !v)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          </div>
                        </div>

                        <InlineError message={signInError} />

                        <button
                          type="submit"
                          disabled={signInLoading}
                          className="w-full py-3 bg-[#0A1F44] text-white rounded-xl text-sm font-semibold hover:bg-[#0d2a5e] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {signInLoading && <Spinner />}
                          {signInLoading ? 'Signing in…' : 'Sign In'}
                        </button>
                      </form>
                    </div>
                  )}

                  {/* ── Sign Up Tab ── */}
                  {activeTab === 'signup' && (
                    <div className="space-y-5">
                      <div>
                        <h2 className="text-xl font-bold text-[#0A1F44]">Create your account</h2>
                        <p className="text-gray-500 text-sm mt-1">
                          Join LegacyBridge Financial Group
                        </p>
                      </div>

                      {signUpSuccess ? (
                        <div className="text-center py-6">
                          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                            <svg
                              className="w-7 h-7 text-green-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </div>
                          <h3 className="font-semibold text-[#0A1F44] mb-2">Check your email</h3>
                          <p className="text-gray-500 text-sm">
                            We sent a confirmation link to <strong>{signUpEmail}</strong>. Click it
                            to activate your account.
                          </p>
                        </div>
                      ) : (
                        <form onSubmit={handleSignUp} className="space-y-4">
                          {/* Google Sign-Up */}
                          <button
                            type="button"
                            onClick={handleGoogleSignIn}
                            disabled={googleLoading}
                            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {googleLoading ? (
                              <Spinner />
                            ) : (
                              <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path
                                  fill="#4285F4"
                                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                  fill="#34A853"
                                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                  fill="#FBBC05"
                                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                                />
                                <path
                                  fill="#EA4335"
                                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                              </svg>
                            )}
                            {googleLoading ? 'Redirecting to Google…' : 'Sign up with Google'}
                          </button>

                          <div className="flex items-center gap-3">
                            <div className="flex-1 h-px bg-gray-200" />
                            <span className="text-xs text-gray-400 font-medium">or with email</span>
                            <div className="flex-1 h-px bg-gray-200" />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                              Full Name
                            </label>
                            <input
                              type="text"
                              value={signUpFullName}
                              onChange={(e) => setSignUpFullName(e.target.value)}
                              placeholder="Jane Smith"
                              autoComplete="name"
                              className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30 focus:border-[#C9A84C] transition-all"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                              Email Address
                            </label>
                            <input
                              type="email"
                              value={signUpEmail}
                              onChange={(e) => setSignUpEmail(e.target.value)}
                              placeholder="you@example.com"
                              autoComplete="email"
                              className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30 focus:border-[#C9A84C] transition-all"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                              Password
                            </label>
                            <div className="relative">
                              <input
                                type={showSignUpPassword ? 'text' : 'password'}
                                value={signUpPassword}
                                onChange={(e) => setSignUpPassword(e.target.value)}
                                placeholder="Min. 6 characters"
                                autoComplete="new-password"
                                className="w-full px-3.5 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30 focus:border-[#C9A84C] transition-all"
                              />
                              <button
                                type="button"
                                onClick={() => setShowSignUpPassword((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showSignUpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                              </button>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                              Confirm Password
                            </label>
                            <div className="relative">
                              <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={signUpConfirm}
                                onChange={(e) => setSignUpConfirm(e.target.value)}
                                placeholder="Re-enter password"
                                autoComplete="new-password"
                                className="w-full px-3.5 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C9A84C]/30 focus:border-[#C9A84C] transition-all"
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                              </button>
                            </div>
                          </div>

                          <InlineError message={signUpError} />

                          <button
                            type="submit"
                            disabled={signUpLoading}
                            className="w-full py-3 bg-[#0A1F44] text-white rounded-xl text-sm font-semibold hover:bg-[#0d2a5e] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {signUpLoading && <Spinner />}
                            {signUpLoading ? 'Creating account…' : 'Create Account'}
                          </button>
                        </form>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            Protected by Supabase Auth · © 2026 LegacyBridge Financial Group
          </p>
        </div>
      </div>
    </div>
  );
}
